/**
 * Game-level coaches on a tournament: people on at least one of its games but not assigned to
 * the tournament itself. The Build Itinerary wizard's "Assign ... to me" adds coaches this way, so
 * every tournament-level view has to count them or the coach looks unassigned.
 */
import type {
  CalendarEventAssignee,
  CalendarEventGameCoach,
  CalendarEventResponse,
} from "../../../../types/api";

type CoachFields = Pick<CalendarEventResponse, "assignees" | "gameCoaches">;

/**
 * The event's game coaches, read defensively: an older API omits the field, so anything that is
 * not an array counts as none. Anyone already an event assignee is dropped so nobody shows twice.
 */
export function eventGameCoaches(event: CoachFields): CalendarEventGameCoach[] {
  const raw: unknown = event.gameCoaches;
  if (!Array.isArray(raw)) return [];
  const assigned = new Set((event.assignees ?? []).map((a) => a.userId));
  return (raw as CalendarEventGameCoach[]).filter((c) => c && !assigned.has(c.userId));
}

/** Event assignees first, then game coaches: everyone covering the event in any way. */
export function allEventCoaches(event: CoachFields): CalendarEventAssignee[] {
  const game = eventGameCoaches(event).map(({ userId, displayName, initials }) => ({
    userId,
    displayName,
    initials,
  }));
  return [...(event.assignees ?? []), ...game];
}

/** True when the user is on the event itself or on any of its games. */
export function isCoveringEvent(event: CoachFields, userId: string | null | undefined): boolean {
  if (!userId) return false;
  return allEventCoaches(event).some((c) => c.userId === userId);
}

/** "1 game" / "3 games". */
export function formatGameCount(count: number): string {
  return `${count} ${count === 1 ? "game" : "games"}`;
}

/** "Also on games: Alex Smith (3), Sam Lee (1)", or null when there are none. */
export function gameCoachesNote(coaches: readonly CalendarEventGameCoach[]): string | null {
  if (coaches.length === 0) return null;
  return `Also on games: ${coaches.map((c) => `${c.displayName} (${c.gameCount})`).join(", ")}`;
}
